import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useBizyExternalOpenStore } from '../store/use-bizy-external-open-store';
import { useBizyPageContextStore } from '../store/use-bizy-page-context-store';
import { BIZY_ROUTE_TRIGGER, useBizySidePanelStore } from '../store/use-bizy-side-panel-store';

// ── URL model (BIZY-680): Bizy is a `?bizy=` search param on the CURRENT page ──
//   ?bizy=chat            → panel open, chat home
//   ?bizy=chat-with-bizy  → an active conversation (id in history.state.bizyConversationId)
//   ?bizy=noticed.<slug>  → person detail in "Bizy noticed" (id in history.state.bizyPersonId)
//
// The person separator is `.` (not `/`) so the value stays readable in the
// address bar — `/` would be percent-encoded to `%2F` by URLSearchParams.
//
// The param value is COSMETIC (the person slug is derived from the display name);
// the sensitive id rides in React Router navigation state (history.state), never
// the URL. Because opening Bizy is a search-param change on the SAME pathname,
// the underlying page never unmounts — no backdrop machinery needed.
//
// Legacy aliases kept for back-compat: `?bizy=open` → chat, `?bizy=fullscreen` →
// chat + fullscreen. (The `bizy_prompt` / `bizy_conversation` / `bizy_settings`
// deep-link params are owned by the deep-link hooks; the `bizy` param is owned here.)

export const BIZY_SEARCH_PARAM = 'bizy';
const CHAT_VALUE = 'chat';
const CONVERSATION_VALUE = 'chat-with-bizy';
const PERSON_PREFIX = 'noticed.';

export interface BizyRouteState {
	bizyConversationId?: string;
	bizyPersonId?: string;
	// The specific "Bizy noticed" moment (card) shown when this entry was pushed,
	// so Back/Forward restores the exact card rather than re-defaulting to the
	// newest. In history state, not the URL — the URL carries only the cosmetic
	// slug. Absent on cold loads / entries pushed before a moment resolved, where
	// the person view's newest-moment default applies (self-consistent: that entry
	// displayed the default too).
	bizyObservedMomentId?: string;
	// How many history entries this Bizy visit has pushed up to AND INCLUDING the
	// entry carrying this state (open = 1, each conversation/person push = prior
	// depth + 1). Closing reads the CURRENT entry's depth and pops exactly that
	// many, landing on the pre-open page. Carried in the entry itself (not a ref)
	// so browser Back — which the panel can't observe counting-wise — naturally
	// leaves the right depth on whatever entry is current. Absent (0) on cold
	// loads / pasted links / URL-driven opens, where close replace-strips instead.
	bizyDepth?: number;
}

export type BizyRouteTarget =
	| { kind: 'chat' }
	| { kind: 'conversation' }
	| { kind: 'person'; slug: string }
	| { kind: 'open' } // legacy alias → chat
	| { kind: 'fullscreen' }; // legacy alias → chat + fullscreen

/**
 * Slugify arbitrary text into a lowercase, hyphenated, ascii-safe segment.
 * Cosmetic only — never reversed.
 */
export function slugify(input: string | null | undefined): string {
	if (!input) return '';
	// NFKD decomposes accented letters into a base ascii letter + a combining
	// mark; the [^a-z0-9] filter then drops the mark, keeping the base letter
	// (e.g. "José" → "jose"). Cosmetic only, so we don't need perfect fidelity.
	return input
		.normalize('NFKD')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 60)
		.replace(/-+$/, '');
}

/** Person slug from a display name: first name + last initial (e.g. `jordan-m`). */
export function personSlug(name: string | null | undefined): string {
	if (!name) return '';
	const parts = name.trim().split(/\s+/);
	const first = parts[0] ?? '';
	const lastInitial = parts.length > 1 ? parts[parts.length - 1][0] : '';
	return slugify(lastInitial ? `${first} ${lastInitial}` : first);
}

/** Parse the `?bizy=` param into a route target, or null when absent/unrecognized. */
export function parseBizyParam(search: string): BizyRouteTarget | null {
	const value = new URLSearchParams(search).get(BIZY_SEARCH_PARAM);
	if (!value) return null;
	if (value === CHAT_VALUE) return { kind: 'chat' };
	if (value === CONVERSATION_VALUE) return { kind: 'conversation' };
	if (value === 'open') return { kind: 'open' };
	if (value === 'fullscreen') return { kind: 'fullscreen' };
	if (value.startsWith(PERSON_PREFIX)) {
		const slug = value.slice(PERSON_PREFIX.length);
		if (slug) return { kind: 'person', slug };
	}
	return null;
}

// Param builders (the value that goes into `?bizy=`).
export const bizyChatValue = () => CHAT_VALUE;
export const bizyConversationValue = () => CONVERSATION_VALUE;
export const bizyPersonValue = (name?: string | null) => `${PERSON_PREFIX}${personSlug(name) || 'person'}`;

/** Return the given search string with `bizy` set to value, other params preserved. */
function searchWithBizy(search: string, value: string): string {
	const params = new URLSearchParams(search);
	params.set(BIZY_SEARCH_PARAM, value);
	return `?${params.toString()}`;
}

/** Return the given search string with `bizy` removed, other params preserved. */
function searchWithoutBizy(search: string): string {
	const params = new URLSearchParams(search);
	params.delete(BIZY_SEARCH_PARAM);
	const next = params.toString();
	return next ? `?${next}` : '';
}

// Only explicit user opens/closes drive the URL. Auto-open (preference, trial,
// navbar restore), deep links, and notification peeks keep the panel as an
// overlay on the current page without writing the param.
const USER_INTENT_TRIGGERS = new Set(['user', 'keyboard']);

function readRouteState(state: unknown): BizyRouteState {
	return (state as BizyRouteState | null) ?? {};
}

/**
 * Two-way sync between the Bizy side-panel store and the `?bizy=` search param
 * (BIZY-680). Mounted from {@link useBizyLayout} for the member web app only.
 *
 * Route → store: a `?bizy=` param (direct nav / back-forward / legacy alias)
 * opens the panel and drives it to the matching view. The param gives the view
 * KIND; the id comes from `location.state`. With no usable state the panel
 * degrades to chat home — the cosmetic slug is never reversed.
 *
 * Store → route: a user-initiated open writes `?bizy=chat` (or `?bizy=chat-with-bizy`
 * + id when a conversation is already active); opening a conversation writes
 * `?bizy=chat-with-bizy`; viewing a person writes `?bizy=noticed.<slug>` — pushing
 * per card so browser Back walks the cards. Closing strips the param (pathname
 * unchanged, so the user simply stays on the page). Only user/keyboard opens
 * write the URL.
 *
 * Because every write keeps `location.pathname`, the page under the overlay never
 * unmounts.
 */
export function useBizyRouteSync(enabled: boolean) {
	const location = useLocation();
	const navigate = useNavigate();

	const isOpen = useBizySidePanelStore((state) => state.isOpen);
	const viewedPerson = useBizySidePanelStore((state) => state.viewedPerson);
	const activeConversationId = useBizySidePanelStore((state) => state.activeConversationId);
	const lastTrigger = useBizySidePanelStore((state) => state.lastTrigger);

	// A conversation deep link (param or `?bizy_conversation=`) queues a pending
	// load before `activeConversationId` catches up; treat that as the intended
	// conversation so store→route doesn't collapse the URL mid-load.
	const pendingConversationId = useBizyExternalOpenStore((state) => state.pendingRequest?.conversationId);

	// True while the current open is tied to a `?bizy=` param (direct nav /
	// back-forward), so removing the param (browser Back) closes the panel.
	const urlDrivenOpenRef = useRef(false);

	const prevIsOpenRef = useRef(isOpen);
	const prevConversationIdRef = useRef(activeConversationId);
	const prevPersonIdRef = useRef(viewedPerson?.id ?? null);
	const prevMomentIdRef = useRef(viewedPerson?.observedMomentId ?? null);
	const prevSearchRef = useRef(location.search);

	// ── Route → store ──
	useEffect(() => {
		if (!enabled) return;

		const { pathname, search } = location;
		const routeState = readRouteState(location.state);
		const prevSearch = prevSearchRef.current;
		prevSearchRef.current = search;

		const target = parseBizyParam(search);
		const store = useBizySidePanelStore.getState();

		if (!target) {
			// No `?bizy=` param. If this open was URL-driven (e.g. browser Back
			// removed the param), close the panel so state follows the URL.
			const hadBizyParam = parseBizyParam(prevSearch) !== null;
			if (hadBizyParam && urlDrivenOpenRef.current && store.isOpen) {
				urlDrivenOpenRef.current = false;
				store.close(BIZY_ROUTE_TRIGGER);
			}
			return;
		}

		urlDrivenOpenRef.current = true;

		// Legacy aliases normalize onto the steady-state param.
		if (target.kind === 'open' || target.kind === 'fullscreen') {
			if (!store.isOpen) store.open(BIZY_ROUTE_TRIGGER, { isFullscreen: target.kind === 'fullscreen' });
			else if (target.kind === 'fullscreen' && !store.isFullscreen) useBizySidePanelStore.setState({ isFullscreen: true });
			store.showChat();
			navigate({ pathname, search: searchWithBizy(search, CHAT_VALUE) }, { replace: true, state: routeState });
			return;
		}

		if (target.kind === 'person') {
			const personId = routeState.bizyPersonId;
			if (!personId) {
				// No usable state (cold load / pasted link): open Bizy without a
				// specific person. Never reverse the slug.
				if (!store.isOpen) store.open(BIZY_ROUTE_TRIGGER);
				store.showChat();
				return;
			}
			if (!store.isOpen) store.open(BIZY_ROUTE_TRIGGER);
			// Restore the exact card this entry was pushed with. A missing moment
			// (cold load / entry pushed before the moment resolved) passes '' and
			// BizyPersonView defaults to the newest once moments load — in that case
			// a concrete moment already in the store for the same person is left
			// alone rather than re-cleared. Same person at a DIFFERENT concrete
			// moment (Back between two cards of one person) must update the card.
			const observedMomentId = routeState.bizyObservedMomentId ?? '';
			const personChanged = store.viewedPerson?.id !== personId;
			const momentChanged = observedMomentId !== '' && store.viewedPerson?.observedMomentId !== observedMomentId;
			if (personChanged || momentChanged) {
				store.viewPerson({ id: personId, name: '', observedMomentId: observedMomentId });
			}
			return;
		}

		if (target.kind === 'conversation') {
			const conversationId = routeState.bizyConversationId;
			if (!store.isOpen) store.open(BIZY_ROUTE_TRIGGER);
			store.showChat();
			if (!conversationId) return; // no usable state → chat home, don't resolve.
			const alreadyOnConversation = store.activeConversationId === conversationId;
			const alreadyLoading = pendingConversationId === conversationId;
			if (!alreadyOnConversation && !alreadyLoading) {
				useBizyExternalOpenStore.getState().setPendingRequest({
					source: 'url_route',
					pagePath: pathname,
					pageContext: useBizyPageContextStore.getState().context.pageType,
					conversationId,
					autoSend: false,
				});
			}
			return;
		}

		// chat
		if (!store.isOpen) store.open(BIZY_ROUTE_TRIGGER);
		store.showChat();
	}, [enabled, location, pendingConversationId, navigate]);

	// ── Store → route ──
	useEffect(() => {
		if (!enabled) return;

		const { pathname, search } = location;
		const routeState = readRouteState(location.state);
		const target = parseBizyParam(search);
		const onBizyParam = target !== null;

		const prevIsOpen = prevIsOpenRef.current;
		const prevConversationId = prevConversationIdRef.current;
		const prevPersonId = prevPersonIdRef.current;
		const prevMomentId = prevMomentIdRef.current;
		const personId = viewedPerson?.id ?? null;
		const momentId = viewedPerson?.observedMomentId ?? null;
		prevIsOpenRef.current = isOpen;
		prevConversationIdRef.current = activeConversationId;
		prevPersonIdRef.current = personId;
		prevMomentIdRef.current = momentId;

		// Open transition: a user-initiated open writes the param onto the current
		// page (pathname preserved) at depth 1. If a conversation is already active,
		// write the conversation param + id so reopening restores the thread's URL.
		if (isOpen && !prevIsOpen) {
			if (!onBizyParam && USER_INTENT_TRIGGERS.has(lastTrigger ?? '')) {
				urlDrivenOpenRef.current = false;
				if (activeConversationId) {
					navigate({ pathname, search: searchWithBizy(search, CONVERSATION_VALUE) }, { state: { bizyConversationId: activeConversationId, bizyDepth: 1 } });
				} else {
					navigate({ pathname, search: searchWithBizy(search, CHAT_VALUE) }, { state: { bizyDepth: 1 } });
				}
			}
			return;
		}

		// Close transition: strip the param, but only a USER-INTENT close (the user
		// deliberately dismissing Bizy) pops history. The CURRENT entry's bizyDepth
		// says how many entries this Bizy visit pushed up to here (browser Back
		// mid-stack leaves the right depth on whatever entry is current), so a user
		// close pops exactly that many — landing on the pre-open page with no
		// overshoot, and Back after close can't reopen Bizy. Any other trigger
		// (`auto`, `navbar_collapse`, …) is a temporary/programmatic close that must
		// not rewrite the user's history position: replace-strip in place, which
		// still removes the param so route→store can't fight it back open. Depth 0
		// (cold load / pasted link / URL-driven open) also replace-strips. A
		// BIZY_ROUTE_TRIGGER close (browser Back already removed the param) hits
		// `!onBizyParam` and stays a no-op. Either way the pathname is unchanged,
		// so the user stays on the page they were on.
		if (!isOpen && prevIsOpen) {
			if (onBizyParam) {
				const depth = routeState.bizyDepth ?? 0;
				const isUserClose = USER_INTENT_TRIGGERS.has(lastTrigger ?? '');
				urlDrivenOpenRef.current = false;
				if (isUserClose && depth > 0) {
					navigate(-depth);
				} else {
					navigate({ pathname, search: searchWithoutBizy(search) }, { replace: true, state: {} });
				}
			}
			return;
		}

		// Deeper routing only while open and already on a `?bizy=` param.
		if (!isOpen || !onBizyParam) return;

		// Person view: push every card, including paging person→person AND paging
		// between two cards of the SAME person (the sidebar can list one person at
		// several moments), so each card is its own history entry and Back walks
		// them. A same-person moment change only counts as paging when BOTH moments
		// are concrete — the person view's newest-moment defaulting ('' → concrete
		// after a cold load) must not push. The id + moment live in navigation
		// state; the "already synced" check keys off them (not the cosmetic slug)
		// so Back/Forward — which restore them via state — never re-navigate.
		const personPaged = personId !== prevPersonId || Boolean(personId && momentId && prevMomentId && momentId !== prevMomentId);
		if (personId && personPaged) {
			const alreadySynced = target.kind === 'person' && routeState.bizyPersonId === personId && (routeState.bizyObservedMomentId ?? '') === (momentId ?? '');
			if (!alreadySynced) {
				navigate(
					{ pathname, search: searchWithBizy(search, bizyPersonValue(viewedPerson?.name)) },
					{
						state: {
							bizyPersonId: personId,
							...(momentId ? { bizyObservedMomentId: momentId } : {}),
							bizyDepth: (routeState.bizyDepth ?? 0) + 1,
						},
					}
				);
			}
			return;
		}
		if (!personId && prevPersonId && target.kind === 'person') {
			// Replace keeps the entry count, so the collapsed-to entry carries the
			// same depth as the entry it replaces.
			navigate({ pathname, search: searchWithBizy(search, CHAT_VALUE) }, { replace: true, state: { bizyDepth: routeState.bizyDepth } });
			return;
		}

		// Conversation: push when a new one is opened; collapse to chat when cleared.
		// Skipped while a person is shown so a background conversation-id change
		// can't yank the URL off the person view.
		if (!personId && activeConversationId && activeConversationId !== prevConversationId) {
			const alreadySynced = target.kind === 'conversation' && routeState.bizyConversationId === activeConversationId;
			if (!alreadySynced) {
				navigate(
					{ pathname, search: searchWithBizy(search, CONVERSATION_VALUE) },
					{ state: { bizyConversationId: activeConversationId, bizyDepth: (routeState.bizyDepth ?? 0) + 1 } }
				);
			}
			return;
		}
		if (!activeConversationId && prevConversationId && target.kind === 'conversation') {
			// Replace keeps the entry count, so depth carries through the collapse.
			navigate({ pathname, search: searchWithBizy(search, CHAT_VALUE) }, { replace: true, state: { bizyDepth: routeState.bizyDepth } });
		}
	}, [enabled, isOpen, viewedPerson, activeConversationId, lastTrigger, location, navigate]);
}
